const AUTH_TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const TOKEN_EXPIRES_AT_KEY = 'tokenExpiresAt';
const SHOULD_MIRROR_AUTH_STORAGE_FOR_TESTS = import.meta.env.MODE === 'test';

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

function getSessionStorageValue(key: string): string | null {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }

  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function setSessionStorageValue(key: string, value: string): boolean {
  const storage = getSessionStorage();
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(key, value);
    return true;
  } catch {
    // Auth recovery should continue even if a browser rejects session storage.
    return false;
  }
}

function removeSessionStorageValue(key: string): void {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(key);
  } catch {
    // Ignore cleanup failures.
  }
}

function clearLegacyLocalAuthStorage(): void {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  if (
    SHOULD_MIRROR_AUTH_STORAGE_FOR_TESTS ||
    typeof storage.removeItem !== 'function'
  ) {
    return;
  }

  removeLegacyLocalStorageValue(AUTH_TOKEN_KEY);
  removeLegacyLocalStorageValue(REFRESH_TOKEN_KEY);
  removeLegacyLocalStorageValue(TOKEN_EXPIRES_AT_KEY);
}

function getLegacyLocalStorageValue(key: string): string | null {
  const storage = getLocalStorage();
  if (!storage || typeof storage.getItem !== 'function') {
    return null;
  }

  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function removeLegacyLocalStorageValue(key: string): void {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    if (typeof storage.removeItem === 'function') {
      storage.removeItem(key);
    }
  } catch {
    // Ignore unreadable legacy storage; auth recovery must not block app boot.
  }
}

function removeTestLocalStorageValue(key: string): void {
  if (!SHOULD_MIRROR_AUTH_STORAGE_FOR_TESTS) {
    return;
  }

  removeLegacyLocalStorageValue(key);
}

export function getStoredAuthToken(): string | null {
  if (SHOULD_MIRROR_AUTH_STORAGE_FOR_TESTS) {
    return getLegacyLocalStorageValue(AUTH_TOKEN_KEY);
  }

  const storage = getSessionStorage();
  const storedToken = getSessionStorageValue(AUTH_TOKEN_KEY);
  if (storedToken) {
    clearLegacyLocalAuthStorage();
    return storedToken;
  }

  const legacyToken = getLegacyLocalStorageValue(AUTH_TOKEN_KEY);
  if (legacyToken) {
    if (storage && setSessionStorageValue(AUTH_TOKEN_KEY, legacyToken)) {
      clearLegacyLocalAuthStorage();
    }
    return legacyToken;
  }

  clearLegacyLocalAuthStorage();
  return null;
}

export function setStoredAuthToken(token: string): void {
  const storage = getLocalStorage();
  if (
    SHOULD_MIRROR_AUTH_STORAGE_FOR_TESTS &&
    storage &&
    typeof storage.setItem === 'function'
  ) {
    try {
      storage.setItem(AUTH_TOKEN_KEY, token);
    } catch {
      // Tests may install storage shims that throw; session storage remains authoritative.
    }
  }

  clearLegacyLocalAuthStorage();
  setSessionStorageValue(AUTH_TOKEN_KEY, token);
}

export function getStoredTokenExpiresAt(): string | null {
  if (SHOULD_MIRROR_AUTH_STORAGE_FOR_TESTS) {
    return getLegacyLocalStorageValue(TOKEN_EXPIRES_AT_KEY);
  }

  const storage = getSessionStorage();
  const storedExpiresAt = getSessionStorageValue(TOKEN_EXPIRES_AT_KEY);
  if (storedExpiresAt) {
    clearLegacyLocalAuthStorage();
    return storedExpiresAt;
  }

  const legacyExpiresAt = getLegacyLocalStorageValue(TOKEN_EXPIRES_AT_KEY);
  if (legacyExpiresAt) {
    if (
      storage &&
      setSessionStorageValue(TOKEN_EXPIRES_AT_KEY, legacyExpiresAt)
    ) {
      clearLegacyLocalAuthStorage();
    }
    return legacyExpiresAt;
  }

  clearLegacyLocalAuthStorage();
  return null;
}

export function setStoredTokenExpiresAt(expiresAt: string): void {
  const storage = getLocalStorage();
  if (
    SHOULD_MIRROR_AUTH_STORAGE_FOR_TESTS &&
    storage &&
    typeof storage.setItem === 'function'
  ) {
    try {
      storage.setItem(TOKEN_EXPIRES_AT_KEY, expiresAt);
    } catch {
      // Tests may install storage shims that throw; session storage remains authoritative.
    }
  }

  clearLegacyLocalAuthStorage();
  setSessionStorageValue(TOKEN_EXPIRES_AT_KEY, expiresAt);
}

export function getValidStoredAuthToken(nowMs = Date.now()): string | null {
  const token = getStoredAuthToken();
  const expiresAtRaw = getStoredTokenExpiresAt();
  const expiresAt = expiresAtRaw ? Number.parseInt(expiresAtRaw, 10) : 0;

  if (!token || !Number.isFinite(expiresAt) || expiresAt <= nowMs) {
    clearStoredAuthSession();
    return null;
  }

  return token;
}

export function hasValidStoredAuthSession(nowMs = Date.now()): boolean {
  return getValidStoredAuthToken(nowMs) !== null;
}

export function clearStoredAuthSession(): void {
  removeSessionStorageValue(AUTH_TOKEN_KEY);
  removeSessionStorageValue(TOKEN_EXPIRES_AT_KEY);
  removeTestLocalStorageValue(AUTH_TOKEN_KEY);
  removeTestLocalStorageValue(REFRESH_TOKEN_KEY);
  removeTestLocalStorageValue(TOKEN_EXPIRES_AT_KEY);
  clearLegacyLocalAuthStorage();
}

export function getStoredRefreshTokenForRequest(): string | undefined {
  if (!SHOULD_MIRROR_AUTH_STORAGE_FOR_TESTS) {
    return undefined;
  }
  return getLegacyLocalStorageValue(REFRESH_TOKEN_KEY) ?? undefined;
}

export function setStoredRefreshToken(refreshToken: string): void {
  const storage = getLocalStorage();
  if (
    SHOULD_MIRROR_AUTH_STORAGE_FOR_TESTS &&
    storage &&
    typeof storage.setItem === 'function'
  ) {
    try {
      storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } catch {
      // Browser-readable refresh storage is test-only compatibility.
    }
  }
}
